import * as React from 'react';

import { cn } from '../cn.js';

/**
 * Caratuva brand mark — a stylized Pico Caratuva ridge (a real summit in Paraná,
 * Brazil) with a gold sunrise. The forest-green plate and Brazilian-flag-leaning
 * gold accent map onto the design tokens in tokens.css.
 *
 * The SVG uses literal hex so it renders correctly when inlined as a favicon or
 * exported as a static asset. Keep these in sync with `--color-navy-950` (plate),
 * `--color-navy-600` (ridge), and `--color-gold-500` (sunrise) in tokens.css.
 */

export interface LogoMarkProps extends React.SVGAttributes<SVGSVGElement> {
  size?: number | string;
  title?: string;
}

const LogoMark = React.forwardRef<SVGSVGElement, LogoMarkProps>(
  ({ size = 32, title = 'Caratuva', className, ...props }, ref) => (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      role="img"
      aria-label={title}
      className={cn(className)}
      {...props}
    >
      <title>{title}</title>
      <rect width="32" height="32" rx="7" fill="#0F2A1E" />
      <circle cx="20.5" cy="9.5" r="2.1" fill="#F2C744" />
      <path d="M4.5 24 L11 14 L14.5 18.5 L20 10.5 L27.5 24 Z" fill="#00A868" />
      <path
        d="M4.5 24 L11 14 L14.5 18.5 L20 10.5 L27.5 24"
        stroke="#0F2A1E"
        strokeWidth="0.6"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  ),
);
LogoMark.displayName = 'LogoMark';

export interface LogoProps extends React.HTMLAttributes<HTMLSpanElement> {
  size?: number;
  showWordmark?: boolean;
}

const Logo = React.forwardRef<HTMLSpanElement, LogoProps>(
  ({ size = 28, showWordmark = true, className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn('inline-flex items-center gap-2 align-middle', className)}
      {...props}
    >
      <LogoMark size={size} />
      {showWordmark ? (
        <span
          className="font-medium tracking-tight text-navy-950"
          style={{ fontFamily: 'var(--font-serif)', fontSize: `${size * 0.72}px` }}
        >
          Caratuva
        </span>
      ) : null}
    </span>
  ),
);
Logo.displayName = 'Logo';

export { Logo, LogoMark };
